import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MessagesNewService } from '../messages-new/messages.service';
import { Sample } from '../models-new/sample';
import { Plate } from '../models-new/plate';
import { PlateType } from '../constants/PlateType';
import { PlateStatus } from '../constants/Status';
import { AnnouncedService } from '../apg-announced/services/announced.service';
import { ArrayProject } from '../masterdata/models/arrayProject';
import { PlateProcessingService } from '../plate-processing/plate-processing.service';
import { ProcessStep } from '../models-new/processStep';
import { ExtractionProtocolComponentName } from '../constants/ProtocolComponentMapping';
import { Protocol } from '../protocol-configuration/dto/Protocol';
import { BatchTypes } from '../models/process-step';

@Component({
	selector: 'app-sample-batching',
	templateUrl: './sample-batching.component.html',
	styleUrls: ['./sample-batching.component.css']
})
export class SampleBatchingComponent {

	@Input() protocolStepName: string = "";
	@Input() selectedProject: ArrayProject;
	@Input() selectedProtocol: Protocol;
	@Input() batch: Plate;
	@Output() batchCreationEmitter = new EventEmitter<any>();

	selectedLabId: string;
	puid: string;
	blocked: boolean = false;
	batchId: string = "";
	scannedSampleBarcode: string = "";
	sampleData: any = [];
	samplesWithSameBarcode: any[] = [];
	showDuplicateSampleSelectionDialog: boolean = false;
	batchSampleBarcodeSet: Set<string> = new Set();
	batchSamples: Array<Sample> = [];

	ngOnInit() {
		this.selectedLabId = JSON.parse(localStorage.getItem('lab')).lab._id;
		this.puid = localStorage.getItem('PUID');
		if(!this.batch.plateID){
			this.createNewBatch()
		}else{
			this.batchId = this.batch.plateID;
			this.updateBatchSampleBarcodeSet();
			this.scannedSampleBarcode = "";
		}
		this.findAndHighlightElement("sample-barcode-scan-field")
	}

	constructor(private messagesService: MessagesNewService, private announcementService: AnnouncedService,
		private plateProcessingService: PlateProcessingService) { }

	createNewBatch() {
		let prefix = new Date().toISOString().slice(0, 10).replace(/-/g, '');
		this.blocked = true;
		this.plateProcessingService.getBatchId(this.selectedLabId, prefix, BatchTypes.SAMPLE).subscribe({
			next: result => {
				this.blocked = false;
				this.batchId = result.data;
				this.generateBatchData();
			},
			error: error => {
				this.blocked = false;
				this.messagesService.addErrorMessage({
					severity:'error', summary: 'Error occured while getting available batch id',
					detail: error.error.bbApiError.message
				})
			}
		})
	}

	generateBatchData(){
		this.batch = new Plate();
		this.batch.type = PlateType.BATCH;
		this.batch.labID = this.selectedLabId;
		this.batch.lastUpdatedPlateID = this.batchId;
		this.batch.plateID = this.batchId;
		this.batch.priority = 10;
		this.batch.samples = [];
		this.batch.scanHorizontal = true;
		this.batch.status = PlateStatus.BATCH_CREATED;
		this.batch.storagePlate = false;
		this.batch.processSteps = [];
		this.batch.projectID = this.selectedProject?.projectID || "";
		this.batch.beadChips = [];
		this.batch.sampleType = "";
		this.batch.sourcePlates = [];
		this.batch.type = "BATCH";
		this.batch.version = null;

		this.scannedSampleBarcode = "";
		this.batchSampleBarcodeSet.clear();
		this.batchSamples = [];
		this.batchCreationEmitter.emit(this.batch);
	}

	private batchingProcessStep(){
		let processStep = new ProcessStep();
		processStep.step = ExtractionProtocolComponentName.BATCHING
		processStep.startPUID = this.puid;
		processStep.startTimeStamp = new Date()
		processStep.endPUID = this.puid;
		processStep.endTimeStamp = new Date();
		processStep.startEquipments = [];
		processStep.endEquipments = [];
		processStep.action = [];

		return processStep;
	}

	disableScanBarcodeField() {
		return this.batch.status!=PlateStatus.BATCH_CREATED || this.batch.samples.length >= 96;
	}

	getNextSampleCountInBatch() {
		return `Position - ${this.batch.samples.length + 1}`;
	}

	searchAndAddSampleToBatch(scannedSampleBarcode: string) {
		if (scannedSampleBarcode.trim() == "") {
			return
		}
		this.getSampleDetails(scannedSampleBarcode);
	}

	getSampleDetails(scannedSampleBarcode: string) {
		this.blocked = true;
		this.announcementService.getSampleFromDelivery(this.selectedLabId, scannedSampleBarcode).subscribe({
			next: (result) => {
				this.blocked = false
				this.samplesWithSameBarcode = result.data
				if (this.samplesWithSameBarcode.length > 1) {
					this.showDuplicateSampleSelectionDialog = true
				}
				else if (this.samplesWithSameBarcode.length == 1) {
					this.sampleData = result.data[0]
					if(!this.sampleValidationChecks(this.scannedSampleBarcode, this.sampleData)){
						return;
					}
					this.checkIfSampleIsAlreadyBatched();
				}
			},
			error: (_error: any) => {
				this.blocked = false;
				this.messagesService.addWarningMessage({
					severity: "warn", summary: "Sample not eligible for batching.",
					detail: `Sample ${scannedSampleBarcode}  was not announced in the manifest.`
				})
				this.scannedSampleBarcode = "";
			}
		})
	}

	sampleValidationChecks(scannedSampleBarcode: string, sampleData){
		if (!sampleData.activated) {
			this.messagesService.addWarningMessage({
				severity: "warn", summary: "Sample not eligible for batching.",
				detail: `Sample ${scannedSampleBarcode} is not activated.`
			})
			this.scannedSampleBarcode = "";
			return false;
		}
		if (sampleData.receptionStatus === "REMOVED") {
			this.messagesService.addWarningMessage({
				severity: "warn", summary: "Unsuitable sample scanned",
				detail: `Sample ${scannedSampleBarcode} is marked as unsuitable.`
			})
			this.scannedSampleBarcode = "";
			return false;
		}
		if (sampleData.receptionPUID === "SRC" && !sampleData.plated) {
			this.messagesService.addWarningMessage({
				severity: "warn", summary: "SRC sample scanned",
				detail: `Sample ${scannedSampleBarcode} is not eligible to be placed on plate.`
			})
			this.scannedSampleBarcode = "";
			return false;
		}

		return true
	}

	selectedSampleFromDuplicate(sampleObjectId) {
		this.sampleData = this.samplesWithSameBarcode.filter(sample => sample._id = sampleObjectId)[0]
		if(!this.sampleValidationChecks(this.scannedSampleBarcode, this.sampleData)){
			return;
		}
		this.checkIfSampleIsAlreadyBatched();
		this.showDuplicateSampleSelectionDialog = false;
	}

	findAndHighlightElement(elementId: string) {
		let element = document.getElementById(elementId);
		if (element) {
			element.focus();
		}
	}

	checkIfSampleIsAlreadyBatched(){
		if(this.batchSampleBarcodeSet.has(this.sampleData._id)){
			this.messagesService.addWarningMessage({
				severity:'warn', summary: "Sample already present",
				detail: `${this.scannedSampleBarcode} is already present in the batch ${this.batch.plateID}`
			})
			this.scannedSampleBarcode = "";
			return;
		}

		this.blocked = true;
		this.plateProcessingService.checkSamplePresentInPlateType(this.selectedLabId, this.sampleData._id, "BATCH").subscribe({
			next: (result_value)=>{
			  this.blocked = false;
			  if(result_value.present){
				this.messagesService.addWarningMessage({
				  severity: 'warn', summary: 'Invalid Barcode',
				  detail: "Barcode: '"+this.scannedSampleBarcode +"' is already scanned in batch "+ result_value.plateID +". Please scan a different barcode.",
				});
				this.scannedSampleBarcode = "";
				this.sampleData = null;
			  }
			  else{
				this.addSampleToBatch();
			  }
			},
			error: (error) => {
			  this.blocked = false;
			  this.messagesService.addErrorMessage({
				severity:"error", summary:"Error while checking for sample existence in other batches.",
				detail: error.error.bbApiError.message
			  })
			}
		})
	}

	addSampleToBatch() {
		let newSample = this.createNewSample(this.scannedSampleBarcode, "BATCH", this.sampleData.gender, this.sampleData.icbfSampleID);
		if(this.batch.samples.length>0){
			this.addSampleToExistingBatch(newSample);
		}else{
			this.createNewBatchWithSample(newSample);
		}
		this.batchSampleBarcodeSet.add(this.sampleData._id)
		this.findAndHighlightElement('barcode-scanner');
	}

	private createNewBatchWithSample(newSample: Sample) {
		this.batch.samples = [newSample];
		this.batch.processSteps.push(this.batchingProcessStep());
		this.batch.version = 0;
		this.blocked = true;
		this.plateProcessingService.createNewPlate(this.batch).subscribe({
			next: result => {
				this.blocked = false;
				this.batch = result.data;
				this.messagesService.addSuccessMessage({
					severity:"success", summary: "Sample Added!",
					detail: `Batch ${this.batch.plateID} created and sample ${this.scannedSampleBarcode} to added to it.`
				})
				this.scannedSampleBarcode = '';
				this.batchSamples.unshift(newSample);
				this.batchCreationEmitter.emit(this.batch);
			},
			error: error => {
				this.blocked = false;
				this.messagesService.addErrorMessage({
					severity:'error', summary:"Error while creating new batch",
					detail: error.error.bbApiError.message + ". Please click on new batch and try again."
				})
				this.batch.samples = [];
				this.batch.processSteps = [];
				this.scannedSampleBarcode = '';
			}
		});
	}

	private addSampleToExistingBatch(newSample: Sample) {
		this.blocked = true;
		this.plateProcessingService.addSampleToPlate(this.selectedLabId, this.batch.plateID, newSample).subscribe({
			next: (result) => {
				this.blocked = false;
				this.scannedSampleBarcode = '';
				this.batch = result;
				this.messagesService.addSuccessMessage({
					severity: 'success', summary: "Sample Added!",
					detail: "Sample " + this.scannedSampleBarcode + " added to batch " + this.batch.plateID
				});
				this.batchSamples.unshift(newSample);
			},
			error: (error) => {
				this.blocked = false;
				this.messagesService.addErrorMessage({
					severity: 'error', summary: "Sample Not Added!",
					detail: error.error.bbApiError.message
				});
				this.scannedSampleBarcode = '';
			}
		});
	}

	createNewSample(barcode: string, position: string, gender: string, icbfSampleID: number) {
		let newSample = new Sample();
		newSample._id = this.sampleData._id
		newSample.position = position;
		newSample.sampleID = barcode;
		newSample.beadChipID = '';
		newSample.beadChipPosition = '';
		newSample.plateRedo = false;
		newSample.priority = this.sampleData.priority
		newSample.sampleType = this.sampleData.sampleType;
		newSample.singleRedo = false;
		newSample.icbfSampleID = icbfSampleID || null;
		newSample.reRunCount = this.sampleData.reRunCount;
		newSample.autoRerun = this.sampleData.autoRerun;
		newSample.sourcePlateID = this.batch.plateID;
		newSample.sourcePlatePosition = position;
		newSample.qcSampleHistory = [];
		newSample.dueDate = this.sampleData.dueDate;
        newSample.receptionPUID = this.puid;

		let plateSampleTypes = this.batch.sampleType.split(",");
		newSample.selectedSampleType = plateSampleTypes[plateSampleTypes.length - 1];

		if (gender == "M") {
			newSample.gender = "male";
		} else if (gender == "F") {
			newSample.gender = "female";
		}
		else {
			newSample.gender = gender;
		}

		if (this.sampleData.boxID) {
			newSample.boxID = this.sampleData.boxID || null;
			newSample.boxReceptionTimeStamp = this.convertToDate(this.sampleData.boxReceptionTimeStamp)
		}
		newSample.projectID = this.sampleData.projectID
		return newSample;
	}

	convertToDate(date: Date): Date {
		if (!date) {
			return;
		}
		return new Date(date);
	}

	updateBatchSampleBarcodeSet(){
		this.batchSampleBarcodeSet.clear()
		this.batch.samples.forEach( sample => {
			this.batchSampleBarcodeSet.add(sample._id);
		})

		this.batchSamples = this.batch.samples;
		this.batchSamples.reverse();
	}
}
